'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button, Dialog, Form } from '@/shared/ui'

import { CertCodeField } from './cert-code-field'
import { EmailSendField } from './email-send-field'
import { useVerifyEmail } from '../../api/verify-email'
import { useCountdownTimer } from '../../lib/timer'

type TEmailCertForm = { certCode: string }

type Props = {
  email: string
  stepLabel?: string
  onSuccess: (emailCertToken: string) => void
  onClose: () => void
}

export const EmailContent = ({
  email,
  stepLabel,
  onSuccess,
  onClose,
}: Props) => {
  const t = useTranslations('email_verification')
  const tCommon = useTranslations('common')

  const [isCodeSent, setIsCodeSent] = useState(false)

  const { timeLeft, startTimer } = useCountdownTimer()

  const form = useForm<TEmailCertForm>({
    defaultValues: { certCode: '' },
    resolver: zodResolver(
      z.object({ certCode: z.string().min(1, t('code.error.required')) }),
    ),
    reValidateMode: 'onSubmit',
  })

  const { verifyEmailAsync, isVerifying } = useVerifyEmail()

  const handleSendSuccess = (expiresIn: number) => {
    startTimer(expiresIn)

    if (!isCodeSent) {
      setIsCodeSent(true)
    }
  }

  const handleVerify = form.handleSubmit(async ({ certCode }) => {
    if (timeLeft === 0) {
      form.setError('certCode', { message: t('code.error.expired') })

      return
    }

    await verifyEmailAsync(email, certCode, {
      onSuccess,
      onError: message => {
        form.setError('certCode', { message })
      },
    })
  })

  return (
    <>
      <Dialog.Header className="p-6">
        <div className="flex items-center gap-2">
          <Dialog.Title>{t('title')}</Dialog.Title>
          {stepLabel && (
            <span className="typo-label2 font-light text-purple-500">
              {stepLabel}
            </span>
          )}
        </div>
        <Dialog.Description className="hidden">
          이메일 인증을 완료해 주세요
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Body className="flex grow flex-col px-6">
        <p className="typo-label2 whitespace-break-spaces text-gray-500">
          {t('description')}
        </p>

        <div className="mt-10 flex flex-col gap-6">
          <EmailSendField
            email={email}
            isCodeSent={isCodeSent}
            onSendSuccess={handleSendSuccess}
          />

          {isCodeSent && (
            <Form.Root form={form}>
              <CertCodeField timeLeft={timeLeft} />
            </Form.Root>
          )}
        </div>
      </Dialog.Body>

      <Dialog.Footer className="flex-row gap-2.5 p-6">
        <Button
          color="secondary"
          size="large"
          fullWidth
          onClick={onClose}
          className="flex-1"
        >
          {tCommon('button.close')}
        </Button>
        <Button
          size="large"
          fullWidth
          className="flex-1"
          disabled={!isCodeSent || isVerifying || form.formState.isSubmitting}
          onClick={handleVerify}
        >
          {t('button.complete')}
        </Button>
      </Dialog.Footer>
    </>
  )
}
